"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { Check, Image as ImageIcon, Link2, Mail, PenLine, Plus, Smartphone, UserRound, type LucideIcon } from "lucide-react";
import { profileSteps, type StepKey } from "../completion";
import "./completion.css";
import { withPercent } from "@/lib/percent";
type Lang = "fa" | "en";
type ProfileLike = Parameters<typeof profileSteps>[0];
const STEP: Record<StepKey, {
  Icon: LucideIcon;
  fa: [string, string];
  en: [string, string];
}> = {
  avatar: {
    Icon: UserRound,
    fa: ["تصویر پروفایل", "چهره‌ای که دیگران کنار آثارتان می‌بینند"],
    en: ["Profile picture", "The face others see beside your work"]
  },
  banner: {
    Icon: ImageIcon,
    fa: ["بنر صفحه", "تصویر بالای صفحه‌ی شما"],
    en: ["Banner", "The picture across the top of your page"]
  },
  bio: {
    Icon: PenLine,
    fa: ["بیو", "چند خط درباره‌ی خودتان و کارتان"],
    en: ["Bio", "A few lines about you and your work"]
  },
  email: {
    Icon: Mail,
    fa: ["ایمیل", "برای اعلان‌ها و بازیابی حساب"],
    en: ["Email", "For notices and account recovery"]
  },
  mobile: {
    Icon: Smartphone,
    fa: ["شماره موبایل", "لازم برای خرید ریالی"],
    en: ["Mobile number", "Needed to pay in rial"]
  },
  links: {
    Icon: Link2,
    fa: ["یک لینک", "وب‌سایت، تلگرام، اینستاگرام یا دیسکورد"],
    en: ["A link", "Website, Telegram, Instagram or Discord"]
  }
};

/**
 * «پروفایلت را کامل کن» — a ring of how far along the account is, and the steps that are left, each with the way to
 * do it: on the profile page a link to that place in the settings, on the settings page the action itself. A step
 * ticks the moment its save lands (the steps are read from the profile — `completion.ts`), and once every step is
 * done the card is gone for good. Renders nothing until the profile is known.
 */
export default function ProfileCompletion(t0) {
  const $ = _c(44);
  const {
    lang,
    user,
    hrefFor,
    onDo
  } = t0;
  if (!user) {
    return null;
  }
  const isFa = lang === "fa";
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[2] !== hrefFor || $[3] !== isFa || $[4] !== onDo || $[5] !== t || $[6] !== user) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const steps = profileSteps(user);
      const done = steps.filter(_temp).length;
      if (done === steps.length) {
        t7 = null;
        break bb0;
      }
      const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
      const percent = Math.round(done / steps.length * 100);
      const C = 2 * Math.PI * 17;
      const ordered = [...steps.filter(_temp2), ...steps.filter(_temp3)];
      t4 = "pc";
      t5 = "pc-title";
      const t8 = `${withPercent(nf.format(percent), true)} کامل`;
      const t9 = `${percent}% complete`;
      let t10;
      if ($[13] !== t || $[14] !== t8 || $[15] !== t9) {
        t10 = t(t8, t9);
        $[13] = t;
        $[14] = t8;
        $[15] = t9;
        $[16] = t10;
      } else {
        t10 = $[16];
      }
      let t11;
      if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = <circle className="pc-ring-track" cx="21" cy="21" r={17} />;
        $[17] = t11;
      } else {
        t11 = $[17];
      }
      const t12 = C * (1 - done / steps.length);
      let t13;
      if ($[18] !== t12) {
        t13 = <svg viewBox="0 0 42 42" aria-hidden={true}>{t11}<circle className="pc-ring-fill" cx="21" cy="21" r={17} strokeDasharray={C} strokeDashoffset={t12} /></svg>;
        $[18] = t12;
        $[19] = t13;
      } else {
        t13 = $[19];
      }
      const t14 = <span className="pc-ring" role="img" aria-label={t10}>{t13}<b>{nf.format(percent)}</b></span>;
      let t15;
      if ($[20] !== t) {
        t15 = t("\u067E\u0631\u0648\u0641\u0627\u06CC\u0644\u062A \u0631\u0627 \u06A9\u0627\u0645\u0644 \u06A9\u0646", "Complete your profile");
        $[20] = t;
        $[21] = t15;
      } else {
        t15 = $[21];
      }
      let t16;
      if ($[22] !== t15) {
        t16 = <h2 id="pc-title">{t15}</h2>;
        $[22] = t15;
        $[23] = t16;
      } else {
        t16 = $[23];
      }
      const t17 = `${nf.format(done)} از ${nf.format(steps.length)} قدم`;
      const t18 = `${done} of ${steps.length} steps`;
      let t19;
      if ($[24] !== t || $[25] !== t17 || $[26] !== t18) {
        t19 = t(t17, t18);
        $[24] = t;
        $[25] = t17;
        $[26] = t18;
        $[27] = t19;
      } else {
        t19 = $[27];
      }
      let t20;
      if ($[28] !== t19) {
        t20 = <p>{t19}</p>;
        $[28] = t19;
        $[29] = t20;
      } else {
        t20 = $[29];
      }
      let t21;
      if ($[30] !== t16 || $[31] !== t20) {
        t21 = <div className="pc-title">{t16}{t20}</div>;
        $[30] = t16;
        $[31] = t20;
        $[32] = t21;
      } else {
        t21 = $[32];
      }
      if ($[33] !== t14 || $[34] !== t21) {
        t6 = <div className="pc-head">{t14}{t21}</div>;
        $[33] = t14;
        $[34] = t21;
        $[35] = t6;
      } else {
        t6 = $[35];
      }
      t2 = "pc-steps";
      t3 = ordered.map(t22 => {
        const {
          key,
          done: ok
        } = t22;
        const st = STEP[key];
        const [label, hint] = isFa ? st.fa : st.en;
        const body = <><span className="pc-mark" aria-hidden={true}>{ok ? <Check /> : <st.Icon />}</span><span className="pc-label">{label}</span>{ok ? <span className="v2-sr-only">{t("\u2014 \u0627\u0646\u062C\u0627\u0645 \u0634\u062F", "\u2014 done")}</span> : <Plus className="pc-add" aria-hidden={true} />}</>;
        return <li key={key} data-done={ok || undefined}>{ok ? <span className="pc-step" title={t("\u0627\u0646\u062C\u0627\u0645 \u0634\u062F", "Done")}>{body}</span> : hrefFor ? <Link href={hrefFor(key)} className="pc-step v2-focus" title={hint}>{body}</Link> : <button type="button" className="pc-step v2-focus" title={hint} onClick={() => onDo?.(key)}>{body}</button>}</li>;
      });
    }
    $[2] = hrefFor;
    $[3] = isFa;
    $[4] = onDo;
    $[5] = t;
    $[6] = user;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[36] !== t2 || $[37] !== t3) {
    t8 = <ol className={t2}>{t3}</ol>;
    $[36] = t2;
    $[37] = t3;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  let t9;
  if ($[39] !== t4 || $[40] !== t5 || $[41] !== t6 || $[42] !== t8) {
    t9 = <section className={t4} aria-labelledby={t5}>{t6}{t8}</section>;
    $[39] = t4;
    $[40] = t5;
    $[41] = t6;
    $[42] = t8;
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  return t9;
}
function _temp3(x_0) {
  return x_0.done;
}
function _temp2(x) {
  return !x.done;
}
function _temp(s) {
  return s.done;
}